import React, { useCallback, useState } from "react";
import { Icon } from "./icons";
import { useDismiss } from "./ui";
import { PRESETS, dayStart, rangeLabel, toDay, useFilters, windowOf } from "./filters";

const DAY = 86_400_000;
const DOW = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

/** Every day cell of one month (UTC), padded to whole Monday-first weeks. */
function monthDays(year, month) {
  const first = Date.UTC(year, month, 1);
  const lead = (new Date(first).getUTCDay() + 6) % 7;
  const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => toDay(first + i * DAY))];
}

function Month({ year, month, start, end, hover, onPick, onHover }) {
  const today = toDay(Date.now());
  const lo = start && (end || hover) ? (start < (end || hover) ? start : end || hover) : start;
  const hi = start && (end || hover) ? (start < (end || hover) ? end || hover : start) : start;
  return (
    <div>
      <div style={{ textAlign: "center", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        {new Date(Date.UTC(year, month, 1)).toLocaleDateString(undefined, { month: "long", year: "numeric", timeZone: "UTC" })}
      </div>
      <div className="aa-cal">
        {DOW.map((d) => (
          <div key={d} className="aa-cal-dow">
            {d}
          </div>
        ))}
        {monthDays(year, month).map((d, i) => {
          if (!d) return <div key={`p${i}`} />;
          const inRange = lo && hi && d >= lo && d <= hi;
          const edge = d === lo && d === hi ? "both" : d === lo ? "start" : d === hi ? "end" : undefined;
          return (
            <button
              key={d}
              type="button"
              className="aa-cal-day"
              disabled={d > today}
              data-in={inRange ? "true" : undefined}
              data-edge={inRange ? edge : undefined}
              data-today={d === today ? "true" : undefined}
              onClick={() => onPick(d)}
              onMouseEnter={() => onHover(d)}
            >
              <span>{Number(d.slice(8))}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The page's date control: presets on the left, a two-month range calendar
 * on the right, and whether figures compare against the previous period.
 */
export default function DateRangePicker() {
  const { f, set } = useFilters();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const w = windowOf(f);
  const [draft, setDraft] = useState({ start: null, end: null });
  const [hover, setHover] = useState(null);
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() - 1 };
  });

  const openPicker = () => {
    setDraft(f.range === "custom" ? { start: f.from, end: f.to || f.from } : { start: toDay(w.from), end: toDay(w.to - 1) });
    setOpen((o) => !o);
  };
  const pick = (d) => {
    if (!draft.start || draft.end) setDraft({ start: d, end: null });
    else setDraft(d < draft.start ? { start: d, end: draft.start } : { start: draft.start, end: d });
  };
  const apply = () => {
    if (!draft.start) return;
    set({ range: "custom", from: draft.start, to: draft.end || draft.start });
    setOpen(false);
  };
  const preset = (id) => {
    set({ range: id, from: null, to: null });
    setOpen(false);
  };
  const months = [cursor, { y: cursor.m === 11 ? cursor.y + 1 : cursor.y, m: (cursor.m + 1) % 12 }].map((c) => (c.m < 0 ? { y: c.y - 1, m: 12 + c.m } : c));
  const shift = (k) => setCursor((c) => {
    const t = new Date(Date.UTC(c.y, c.m + k, 1));
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() };
  });
  const compare = f.compare !== "0";
  const span = draft.start ? Math.round(((dayStart(draft.end || draft.start) - dayStart(draft.start)) / DAY) + 1) : 0;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" className="aa-btn" onClick={openPicker} aria-expanded={open}>
        <Icon name="calendar" size={15} />
        <span>{rangeLabel(f)}</span>
        {compare && <span style={{ color: "var(--aa-muted)", fontWeight: 400 }}>vs previous</span>}
        <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="aa-pop" style={{ right: 0, top: 42, display: "flex", width: 680, maxWidth: "calc(100vw - 32px)" }}>
          <div style={{ width: 172, padding: 8, borderRight: "1px solid var(--aa-border)", flex: "none" }}>
            {PRESETS.map((p) => (
              <button key={p.id} type="button" className="aa-menu-item" aria-selected={f.range === p.id} onClick={() => preset(p.id)}>
                <span style={{ flex: 1 }}>{p.label}</span>
                {f.range === p.id && <Icon name="check" size={15} strokeWidth={2.5} style={{ color: "var(--aa-accent)" }} />}
              </button>
            ))}
            <div style={{ height: 1, background: "var(--aa-border)", margin: "8px 4px" }} />
            <button type="button" className="aa-menu-item" aria-selected={f.range === "custom"} data-active={f.range === "custom"}>
              <span style={{ flex: 1 }}>Custom range</span>
              {f.range === "custom" && <Icon name="check" size={15} strokeWidth={2.5} style={{ color: "var(--aa-accent)" }} />}
            </button>
          </div>
          <div style={{ flex: 1, padding: 16, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 20, position: "relative", overflowX: "auto" }} onMouseLeave={() => setHover(null)}>
              <button type="button" className="aa-btn aa-btn-ghost aa-btn-icon aa-btn-sm" style={{ position: "absolute", left: 0, top: -4 }} onClick={() => shift(-1)} aria-label="Previous month">
                <Icon name="chevronLeft" size={15} />
              </button>
              <button type="button" className="aa-btn aa-btn-ghost aa-btn-icon aa-btn-sm" style={{ position: "absolute", right: 0, top: -4 }} onClick={() => shift(1)} aria-label="Next month">
                <Icon name="chevronRight" size={15} />
              </button>
              {months.map((mm) => (
                <Month key={`${mm.y}-${mm.m}`} year={mm.y} month={mm.m} start={draft.start} end={draft.end} hover={draft.end ? null : hover} onPick={pick} onHover={setHover} />
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, borderTop: "1px solid var(--aa-border)", marginTop: 14, paddingTop: 12, flexWrap: "wrap" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--aa-ink-2)", cursor: "pointer" }}>
                <input type="checkbox" checked={compare} onChange={(e) => set({ compare: e.target.checked ? "1" : "0" })} style={{ accentColor: "var(--aa-accent)" }} />
                Compare to previous period
              </label>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 12, color: "var(--aa-muted)" }}>
                {draft.start ? `${span} day${span === 1 ? "" : "s"} · UTC` : "Pick a start day"}
              </span>
              <button type="button" className="aa-btn aa-btn-sm" onClick={close}>
                Cancel
              </button>
              <button type="button" className="aa-btn aa-btn-sm aa-btn-primary" disabled={!draft.start} onClick={apply}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
